import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { HttpService } from './http.service';

const CUSTOMER_LOYALTY_APP_URL = 'https://rewards.urban-eatery.co.uk';

export interface LoyaltyProgramme {
  restaurant_id: string;
  status: string;
  programme_name: string;
  description: string;
  currency: string;
  terms_version: string;
  terms_url: string;
  privacy_url: string;
  point_value_pence: number;
  minimum_points: number;
  increment_points: number;
  maximum_order_percentage: number;
  points_per_spend_unit: number;
  spend_pence_per_point_unit: number;
  minimum_order_pence: number;
  redemption_minimum_order_pence: number;
  tiers: LoyaltyTier[];
  is_active: boolean;
}

export interface LoyaltyTier {
  tier_id: string;
  name: string;
  threshold: number;
  multiplier_basis_points: number;
  benefits: string[];
}

export interface LoyaltyOverview {
  current_points: number;
  pending_points: number;
  reserved_points: number;
  lifetime_points: number;
  redeemed_points: number;
  qualifying_points: number;
  tier_name: string;
  next_tier_name: string;
  current_tier_points: number;
  next_tier_points: number;
  points_to_next_tier: number;
  cashback_value: number;
  public_member_id: string;
  next_expiry_points: number;
  next_expiry_at: string;
  tier_multiplier?: number;
  tier_bonus_percent?: number;
  tier_bonus_text?: string;
  next_tier_bonus_percent?: number;
}

export interface LoyaltyReward {
  _id: string;
  reward_id?: string;
  title: string;
  description: string;
  type: string;
  points_required: number;
  discount_amount: number;
  percentage: number;
  minimum_order_amount?: number;
  expires_in_days: number;
  is_active?: boolean;
}

export interface LoyaltyTransaction {
  type: 'earned' | 'redeemed' | string;
  ledger_type: string;
  title: string;
  points: number;
  status: string;
  order_id: string;
  balance_after: number;
  created_at: string;
}

export interface LoyaltyWalletState {
  joined: boolean;
  membership_status: string;
  public_member_id: string;
  overview: LoyaltyOverview;
  wallet: any;
  membership: any;
  next_expiry: any;
}

export interface LoyaltySummary {
  joined: boolean;
  programme: LoyaltyProgramme | null;
  overview: LoyaltyOverview;
  rewards: LoyaltyReward[];
  transactions: LoyaltyTransaction[];
}

export interface LoyaltyAccessState {
  allowed: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoyaltyService {
  private readonly signupIntentKey = 'loyalty_auto_join_after_signup';
  private readonly guestPromptKey = 'loyalty_guest_prompt_shown';

  constructor(private httpService: HttpService) {}

  rememberSignupAutoJoin(programme: LoyaltyProgramme | null) {
    if (!programme || typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(this.signupIntentKey, JSON.stringify({
      restaurant_id: programme.restaurant_id,
      terms_version: programme.terms_version
    }));
  }

  handlePostAuthentication(user: any): Promise<void> {
    if (!user || !user._id || user.user_type === 'guest_user') {
      return Promise.resolve();
    }

    return this.getProgramme().toPromise().then((programme) => {
      if (!programme || !programme.is_active) {
        this.clearSignupAutoJoin();
        return;
      }

      return this.getWalletState(programme).toPromise().then((walletState) => {
        if (walletState.joined) {
          this.clearSignupAutoJoin();
          return;
        }

        const signupIntent = this.getSignupAutoJoin();
        if (signupIntent && signupIntent.restaurant_id === programme.restaurant_id) {
          return this.join(programme.terms_version || signupIntent.terms_version || '1', {}).toPromise()
            .then(() => {
              this.clearSignupAutoJoin();
              return Swal.fire({
                icon: 'success',
                title: `Welcome to ${programme.programme_name}!`,
                text: 'Your loyalty membership is ready. You can now earn points and manage rewards in the loyalty app.',
                confirmButtonText: 'Open rewards app',
                showCancelButton: true,
                cancelButtonText: 'Continue here'
              }).then((result: any) => {
                if (result.isConfirmed) {
                  this.openCustomerApp(programme.restaurant_id);
                }
              });
            }, () => {
              this.clearSignupAutoJoin();
              Swal.fire('Oops!', 'Could not join the loyalty programme, please try again.', 'warning');
            });
        }

        return Swal.fire({
          icon: 'info',
          title: `Join ${programme.programme_name}?`,
          html: `Earn points and unlock restaurant rewards. ` +
            (programme.terms_url ? `<a href="${programme.terms_url}" target="_blank" rel="noopener">View loyalty terms</a>.` : ''),
          confirmButtonText: 'Join loyalty programme',
          showCancelButton: true,
          cancelButtonText: 'Not now'
        }).then((result: any) => {
          if (!result.isConfirmed) {
            return;
          }
          return this.join(programme.terms_version || '1', {}).toPromise().then(() =>
            Swal.fire({
              icon: 'success',
              title: 'You have joined!',
              text: 'Your loyalty membership is ready.',
              confirmButtonText: 'Open rewards app',
              showCancelButton: true,
              cancelButtonText: 'Continue here'
            }).then((joinedResult: any) => {
              if (joinedResult.isConfirmed) {
                this.openCustomerApp(programme.restaurant_id);
              }
            }),
            () => { Swal.fire('Oops!', 'Could not join the loyalty programme, please try again.', 'warning'); }
          );
        });
      });
    }).catch(() => undefined);
  }

  showGuestSignupInvitation(): Promise<void> {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(this.guestPromptKey)) {
      return Promise.resolve();
    }
    return this.getProgramme().toPromise().then((programme) => {
      if (!programme || !programme.is_active) {
        return;
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(this.guestPromptKey, 'true');
      }
      return Swal.fire({
        icon: 'info',
        title: `Earn rewards with ${programme.programme_name}`,
        text: 'Create a registered account to join the loyalty programme and earn points on eligible orders. You can still continue as a guest.',
        confirmButtonText: 'Create account',
        showCancelButton: true,
        cancelButtonText: 'Continue as guest'
      }).then((result: any) => {
        if (result.isConfirmed) {
          window.location.href = '/sign-up';
        }
      });
    }).catch(() => undefined);
  }

  openCustomerApp(restaurantId?: string) {
    if (typeof window === 'undefined') {
      return;
    }
    const separator = CUSTOMER_LOYALTY_APP_URL.indexOf('?') >= 0 ? '&' : '?';
    const restaurantQuery = restaurantId ? `${separator}restaurant_id=${encodeURIComponent(restaurantId)}` : '';
    window.open(`${CUSTOMER_LOYALTY_APP_URL}/wallet${restaurantQuery}`, '_blank', 'noopener');
  }

  private getSignupAutoJoin(): any {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      return JSON.parse(sessionStorage.getItem(this.signupIntentKey) || 'null');
    } catch (_) {
      return null;
    }
  }

  private clearSignupAutoJoin() {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(this.signupIntentKey);
    }
  }

  // ---------------------------------------------------------------------------
  // Access gate (synchronous, safe to call from templates)
  // ---------------------------------------------------------------------------
  getAccessState(user: any, _restaurantId?: any): LoyaltyAccessState {
    if (!user || !user._id) {
      return {
        allowed: false,
        message: 'Sign in with a registered account to use loyalty rewards.'
      };
    }

    if (user.user_type === 'guest_user') {
      return {
        allowed: false,
        message: 'Loyalty rewards are only available to registered users.'
      };
    }

    return { allowed: true, message: '' };
  }

  // ---------------------------------------------------------------------------
  // Public discovery
  // ---------------------------------------------------------------------------
  getProgramme(restaurantId?: string): Observable<LoyaltyProgramme | null> {
    return this.httpService.getLoyaltyProgramme(restaurantId).pipe(
      map((res: any) => this.normalizeProgramme(this.extractPayload(res))),
      catchError(() => of(null))
    );
  }

  getPublicRewards(restaurantId?: string): Observable<LoyaltyReward[]> {
    return this.httpService.getLoyaltyPublicRewards(restaurantId).pipe(
      map((res: any) => this.normalizeRewards(res)),
      catchError(() => of([]))
    );
  }

  // ---------------------------------------------------------------------------
  // Membership
  // ---------------------------------------------------------------------------
  join(acceptedTermsVersion: string, marketingPreferences: any = {}): Observable<any> {
    return this.httpService.joinLoyaltyMembership({
      accepted_terms_version: acceptedTermsVersion,
      marketing_preferences: marketingPreferences || {}
    });
  }

  leave(): Observable<any> {
    return this.httpService.leaveLoyaltyMembership();
  }

  // ---------------------------------------------------------------------------
  // Wallet, transactions, eligible rewards
  // ---------------------------------------------------------------------------
  getWalletState(programme: LoyaltyProgramme | null = null): Observable<LoyaltyWalletState> {
    return this.httpService.getLoyaltyWallet().pipe(
      map((res: any) => this.buildWalletState(this.extractPayload(res), programme, true)),
      catchError(() => of(this.buildWalletState(null, programme, false)))
    );
  }

  getTransactions(cursor?: string, limit: number = 25): Observable<{ items: LoyaltyTransaction[]; next_cursor: string | null }> {
    return this.httpService.getLoyaltyTransactions(cursor, limit).pipe(
      map((res: any) => {
        const payload = this.extractPayload(res);
        const list = Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []);
        return {
          items: this.normalizeTransactions(list),
          next_cursor: payload.next_cursor || null
        };
      }),
      catchError(() => of({ items: [], next_cursor: null }))
    );
  }

  getEligibleRewards(): Observable<LoyaltyReward[]> {
    return this.httpService.getLoyaltyEligibleRewards().pipe(
      map((res: any) => this.normalizeRewards(res)),
      catchError(() => of([]))
    );
  }

  getIdentityToken(): Observable<any> {
    return this.httpService.createLoyaltyIdentityToken().pipe(
      map((res: any) => this.extractPayload(res)),
      catchError(() => of(null))
    );
  }

  earnPreview(orderId: string): Observable<any> {
    return this.httpService.loyaltyEarnPreview({ order_id: orderId }).pipe(
      map((res: any) => this.extractPayload(res)),
      catchError(() => of(null))
    );
  }

  // ---------------------------------------------------------------------------
  // Redemption reservation flow
  // ---------------------------------------------------------------------------
  quote(orderId: string): Observable<any> {
    return this.httpService.loyaltyRedemptionQuote({ order_id: orderId }).pipe(
      map((res: any) => this.extractPayload(res))
    );
  }

  reserve(points: number, checkoutId: string, orderId?: string, orderSubtotalPence?: number, rewardId?: string): Observable<any> {
    return this.httpService.loyaltyReserve({
      points,
      checkout_id: checkoutId,
      order_id: orderId,
      order_subtotal_pence: orderSubtotalPence || 0,
      reward_id: rewardId || undefined
    }).pipe(
      map((res: any) => this.extractPayload(res))
    );
  }

  release(reservationId: string): Observable<any> {
    return this.httpService.loyaltyRelease(reservationId);
  }

  // ---------------------------------------------------------------------------
  // Composite summary (used by checkout for a single-shot load)
  // ---------------------------------------------------------------------------
  getLoyaltySummary(): Observable<LoyaltySummary> {
    return forkJoin([
      this.getProgramme(),
      this.getWalletState(),
      this.getEligibleRewards(),
      this.getTransactions()
    ]).pipe(
      map(([programme, walletState, rewards, transactionsPage]) => ({
        joined: walletState.joined,
        programme,
        overview: walletState.joined
          ? this.buildOverview(walletState.wallet, walletState.membership, walletState.next_expiry, programme)
          : this.createEmptyOverview(),
        rewards,
        transactions: transactionsPage.items
      }))
    );
  }

  // ---------------------------------------------------------------------------
  // Defaults and mappers
  // ---------------------------------------------------------------------------
  createEmptyOverview(): LoyaltyOverview {
    return {
      current_points: 0,
      pending_points: 0,
      reserved_points: 0,
      lifetime_points: 0,
      redeemed_points: 0,
      qualifying_points: 0,
      tier_name: 'Starter',
      next_tier_name: 'Bronze',
      current_tier_points: 0,
      next_tier_points: 0,
      points_to_next_tier: 0,
      cashback_value: 0,
      public_member_id: '',
      next_expiry_points: 0,
      next_expiry_at: '',
      tier_multiplier: 1,
      tier_bonus_percent: 0,
      tier_bonus_text: 'Standard rate (1x)',
      next_tier_bonus_percent: 0
    };
  }

  private buildWalletState(payload: any, programme: LoyaltyProgramme | null, joined: boolean): LoyaltyWalletState {
    const wallet = payload && payload.wallet ? payload.wallet : null;
    const membership = payload && payload.membership ? payload.membership : null;
    const nextExpiry = payload && payload.next_expiry ? payload.next_expiry : null;
    const membershipStatus = membership ? membership.status : '';
    const reallyJoined = joined && !!wallet && membershipStatus !== 'left';

    return {
      joined: reallyJoined,
      membership_status: membershipStatus,
      public_member_id: membership ? membership.public_member_id : '',
      wallet,
      membership,
      next_expiry: nextExpiry,
      overview: reallyJoined
        ? this.buildOverview(wallet, membership, nextExpiry, programme)
        : this.createEmptyOverview()
    };
  }

  buildOverview(wallet: any, membership: any, nextExpiry: any, programme: LoyaltyProgramme | null): LoyaltyOverview {
    const overview = this.createEmptyOverview();
    if (!wallet) {
      return overview;
    }

    overview.current_points = Number(wallet.available_points || 0);
    overview.pending_points = Number(wallet.pending_points || 0);
    overview.reserved_points = Number(wallet.reserved_points || 0);
    overview.lifetime_points = Number(wallet.lifetime_earned_points || 0);
    overview.redeemed_points = Number(wallet.lifetime_redeemed_points || 0);
    overview.qualifying_points = Number(wallet.qualifying_points || 0);
    overview.public_member_id = membership ? (membership.public_member_id || '') : '';

    if (nextExpiry) {
      overview.next_expiry_points = Number(nextExpiry.points || 0);
      overview.next_expiry_at = nextExpiry.expires_at || '';
    }

    if (programme && programme.point_value_pence) {
      overview.cashback_value = Number(((overview.current_points * programme.point_value_pence) / 100).toFixed(2));
    }

    this.applyTierProgress(overview, membership, programme);
    return overview;
  }

  private applyTierProgress(overview: LoyaltyOverview, membership: any, programme: LoyaltyProgramme | null) {
    const tiers = (programme && Array.isArray(programme.tiers)) ? programme.tiers.slice() : [];
    if (!tiers.length) {
      overview.tier_name = 'Member';
      overview.next_tier_name = '';
      overview.current_tier_points = 0;
      overview.next_tier_points = 0;
      overview.points_to_next_tier = 0;
      overview.tier_multiplier = 1;
      overview.tier_bonus_percent = 0;
      overview.tier_bonus_text = 'Standard rate (1x)';
      overview.next_tier_bonus_percent = 0;
      return;
    }

    tiers.sort((a, b) => a.threshold - b.threshold);
    const metric = overview.qualifying_points;

    let currentTier = tiers[0];
    let nextTier: LoyaltyTier | null = null;

    for (const tier of tiers) {
      if (metric >= tier.threshold) {
        currentTier = tier;
      } else {
        nextTier = tier;
        break;
      }
    }

    if (membership && membership.current_tier_id) {
      const matched = tiers.find(t => t.tier_id === membership.current_tier_id);
      if (matched) {
        currentTier = matched;
      }
    }

    const multiplierBasisPoints = currentTier ? (currentTier.multiplier_basis_points || 10000) : 10000;
    const bonusPercent = multiplierBasisPoints > 10000 ? Math.round((multiplierBasisPoints - 10000) / 100) : 0;
    const multiplierValue = Number((multiplierBasisPoints / 10000).toFixed(2));

    const nextMultiplierBasisPoints = nextTier ? (nextTier.multiplier_basis_points || 10000) : multiplierBasisPoints;
    const nextBonusPercent = nextMultiplierBasisPoints > 10000 ? Math.round((nextMultiplierBasisPoints - 10000) / 100) : 0;

    overview.tier_name = currentTier ? currentTier.name : 'Member';
    overview.next_tier_name = nextTier ? nextTier.name : 'Top tier';
    overview.current_tier_points = currentTier ? currentTier.threshold : 0;
    overview.next_tier_points = nextTier ? nextTier.threshold : (currentTier ? currentTier.threshold : 0);
    overview.points_to_next_tier = nextTier ? Math.max(nextTier.threshold - metric, 0) : 0;
    overview.tier_multiplier = multiplierValue;
    overview.tier_bonus_percent = bonusPercent;
    overview.tier_bonus_text = bonusPercent > 0 ? `+${bonusPercent}% bonus points (${multiplierValue}x)` : 'Standard rate (1x)';
    overview.next_tier_bonus_percent = nextBonusPercent;
  }

  normalizeProgramme(payload: any): LoyaltyProgramme | null {
    if (!payload || !payload.restaurant_id) {
      return null;
    }
    const redemption = payload.redemption || {};
    const earning = payload.earning || {};
    const tiers: LoyaltyTier[] = Array.isArray(payload.tiers) ? payload.tiers.map((tier: any) => ({
      tier_id: tier.tier_id || '',
      name: tier.name || 'Tier',
      threshold: Number(tier.threshold || 0),
      multiplier_basis_points: Number(tier.multiplier_basis_points || 10000),
      benefits: Array.isArray(tier.benefits) ? tier.benefits : []
    })) : [];

    return {
      restaurant_id: payload.restaurant_id,
      status: payload.status || 'draft',
      programme_name: payload.programme_name || 'Loyalty Club',
      description: payload.description || '',
      currency: payload.currency || 'GBP',
      terms_version: payload.terms_version || '',
      terms_url: payload.terms_url || '',
      privacy_url: payload.privacy_url || '',
      point_value_pence: Number(redemption.point_value_pence || 1),
      minimum_points: Number(redemption.minimum_points || 0),
      increment_points: Number(redemption.increment_points || 1),
      maximum_order_percentage: Number(redemption.maximum_order_percentage || 0),
      points_per_spend_unit: Number(earning.points_per_spend_unit || 1),
      spend_pence_per_point_unit: Number(earning.spend_pence_per_point_unit || 100),
      minimum_order_pence: Number(earning.minimum_order_pence || 0),
      redemption_minimum_order_pence: Number(redemption.minimum_order_pence || 0),
      tiers,
      is_active: payload.status === 'active' || payload.status === 'closing'
    };
  }

  normalizeRewards(response: any): LoyaltyReward[] {
    const payload = this.extractPayload(response);
    const list = Array.isArray(payload) ? payload : (Array.isArray(payload.rewards) ? payload.rewards : []);
    return (list || []).map((item: any) => {
      const valuePence = Number(item.value_pence || 0);
      const percentage = Number(item.percentage_basis_points || 0) / 100;
      const minOrderPence = Number(item.minimum_order_pence || 0);
      const minOrderAmount = minOrderPence > 0 ? Number((minOrderPence / 100).toFixed(2)) : (item.min_order_amount ? Number(item.min_order_amount) : 0);
      return {
        _id: item._id || item.reward_id || '',
        reward_id: item.reward_id || item._id || '',
        title: item.name || item.title || 'Reward',
        description: item.description || '',
        type: item.type || 'points_discount',
        points_required: Number(item.points_required || 0),
        discount_amount: valuePence > 0 ? Number((valuePence / 100).toFixed(2)) : 0,
        percentage,
        minimum_order_amount: minOrderAmount,
        expires_in_days: Number(item.expires_in_days || 0),
        is_active: item.status ? item.status === 'active' : true
      } as LoyaltyReward;
    }).filter((item: LoyaltyReward) => !!item._id);
  }

  normalizeTransactions(response: any): LoyaltyTransaction[] {
    const list = Array.isArray(response) ? response : (this.extractPayload(response).items || []);
    return (list || []).map((item: any) => {
      const net = Number(item.available_delta || 0) + Number(item.pending_delta || 0);
      const ledgerType = (item.type || '').toString().toLowerCase();
      const displayType = net >= 0 ? 'earned' : 'redeemed';
      return {
        type: displayType,
        ledger_type: ledgerType,
        title: this.describeLedgerEntry(item),
        points: Math.abs(net || Number(item.reserved_delta || 0)),
        status: item.status || 'posted',
        order_id: item.order_id || '',
        balance_after: item.balance_after && typeof item.balance_after === 'object'
          ? Number(item.balance_after.available || 0)
          : Number(item.balance_after || 0),
        created_at: item.createdAt || item.created_at || new Date().toISOString()
      } as LoyaltyTransaction;
    }).sort((left: LoyaltyTransaction, right: LoyaltyTransaction) =>
      new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
    );
  }

  private describeLedgerEntry(item: any): string {
    const labels: { [key: string]: string } = {
      earn: 'Points earned',
      bonus: 'Bonus points',
      redeem: 'Points redeemed',
      expire: 'Points expired',
      adjust: 'Manual adjustment',
      refund_restore: 'Points restored (refund)',
      earn_reversal: 'Earn reversed',
      redemption_reversal: 'Redemption reversed',
      transfer_pending: 'Pending points released',
      release: 'Reservation released'
    };
    const base = labels[(item.type || '').toString().toLowerCase()] || 'Loyalty activity';
    return item.order_id ? `${base} · Order ${item.order_id}` : base;
  }

  private extractPayload(response: any) {
    return response && response.data !== undefined ? response.data : (response || {});
  }
}
